import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCES = ["google", "linkedin", "instagram", "tiktok", "youtube", "twitter", "email", "whatsapp", "outro"];
const MEDIUMS = ["cpc", "social_organico", "social_pago", "email", "display", "video", "referral", "direto", "outro"];

// ─── Copiar para clipboard ────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <button
      onClick={copy}
      title="Copiar URL"
      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all shrink-0"
      style={{
        background: copied ? "#D1FAE5" : "#EDEBE6",
        color: copied ? "#10B981" : "#0A0A0A",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copiado
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copiar
        </>
      )}
    </button>
  );
}

// ─── Modal nova UTM ───────────────────────────────────────────────────────────

function NovaUtmModal({ clientId, onSave, onClose }: { clientId: number; onSave: () => void; onClose: () => void }) {
  const [nome, setNome] = useState("");
  const [urlBase, setUrlBase] = useState("");
  const [source, setSource] = useState("linkedin");
  const [medium, setMedium] = useState("social_organico");
  const [campaign, setCampaign] = useState("");
  const [content, setContent] = useState("");
  const [term, setTerm] = useState("");
  const [preview, setPreview] = useState("");

  const createMutation = trpc.utms.create.useMutation({
    onSuccess: () => { onSave(); onClose(); },
  });

  function buildPreview() {
    if (!urlBase || !source || !medium || !campaign) return "";
    try {
      const u = new URL(urlBase);
      u.searchParams.set("utm_source", source);
      u.searchParams.set("utm_medium", medium);
      u.searchParams.set("utm_campaign", campaign);
      if (content) u.searchParams.set("utm_content", content);
      if (term) u.searchParams.set("utm_term", term);
      return u.toString();
    } catch { return ""; }
  }

  function handleChange() {
    setPreview(buildPreview());
  }

  const inputClass = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";
  const labelClass = "block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5";

  const urlFinal = buildPreview();
  const canSave = nome.trim() && urlBase.trim() && source && medium && campaign.trim() && !createMutation.isPending;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E7E5E0]">
        <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-[#E7E5E0]">
          <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
            Novo link UTM
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Nome da campanha *</label>
            <input autoFocus type="text" value={nome} onChange={e => { setNome(e.target.value); handleChange(); }}
              placeholder="Ex: Post LinkedIn Reforma Tributária" className={inputClass} />
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>URL base *</label>
            <input type="url" value={urlBase} onChange={e => { setUrlBase(e.target.value); handleChange(); }}
              placeholder="https://emiteai.com.br/..." className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Source *</label>
              <select value={source} onChange={e => { setSource(e.target.value); handleChange(); }} className={inputClass}>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Medium *</label>
              <select value={medium} onChange={e => { setMedium(e.target.value); handleChange(); }} className={inputClass}>
                {MEDIUMS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Campaign *</label>
            <input type="text" value={campaign} onChange={e => { setCampaign(e.target.value); handleChange(); }}
              placeholder="reforma_tributaria_2026" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Content</label>
              <input type="text" value={content} onChange={e => { setContent(e.target.value); handleChange(); }}
                placeholder="post_carousel" className={inputClass} />
            </div>
            <div>
              <label className={labelClass} style={{ fontFamily: "Poppins, sans-serif" }}>Term</label>
              <input type="text" value={term} onChange={e => { setTerm(e.target.value); handleChange(); }}
                placeholder="tributacao" className={inputClass} />
            </div>
          </div>

          {urlFinal && (
            <div className="rounded-xl border border-[#E7E5E0] bg-[#F5F4F1] p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>Preview do link</span>
                <CopyButton text={urlFinal} />
              </div>
              <p className="text-[10px] text-zinc-500 break-all font-mono">{urlFinal}</p>
            </div>
          )}
        </div>

        {createMutation.error && (
          <p className="px-6 py-2 text-red-500 text-sm">{createMutation.error.message}</p>
        )}

        <div className="flex gap-3 px-6 py-4 shrink-0 border-t border-[#E7E5E0]">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Cancelar
          </button>
          <button
            onClick={() => createMutation.mutate({ clientId, nome: nome.trim(), urlBase: urlBase.trim(), utmSource: source, utmMedium: medium, utmCampaign: campaign.trim(), utmContent: content.trim() || undefined, utmTerm: term.trim() || undefined })}
            disabled={!canSave}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
          >
            {createMutation.isPending ? "Salvando…" : "Salvar link"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Página UTMs ──────────────────────────────────────────────────────────────

export default function UTMs() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const [modalOpen, setModalOpen] = useState(false);
  const [busca, setBusca] = useState("");

  const { data: utms = [], isLoading, refetch } = trpc.utms.list.useQuery(
    { clientId }, { enabled: !!clientId }
  );
  const deleteMutation = trpc.utms.delete.useMutation({ onSuccess: () => refetch() });

  const filtered = utms.filter(u =>
    !busca || u.nome.toLowerCase().includes(busca.toLowerCase()) ||
    u.utmCampaign.toLowerCase().includes(busca.toLowerCase()) ||
    u.utmSource.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-[#F5F4F1]">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E0] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-bold text-lg text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>UTMs</h1>
            <p className="text-zinc-400 text-xs mt-0.5">{utms.length} {utms.length === 1 ? "link" : "links"} cadastrado{utms.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="relative flex-1 min-w-48 max-w-72">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar UTMs…"
              className="w-full pl-9 pr-3 py-2 border border-[#E7E5E0] rounded-lg text-sm text-[#0A0A0A] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white" />
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all shrink-0"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novo link
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 rounded-full border-2 border-[#E7E5E0] border-t-[#0A0A0A] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#EDEBE6" }}>
              <svg className="w-8 h-8 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {busca ? "Nenhum link encontrado" : "Nenhuma UTM cadastrada"}
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                {busca ? "Tente outro termo" : "Crie links rastreáveis para suas campanhas"}
              </p>
            </div>
            {!busca && (
              <button onClick={() => setModalOpen(true)} className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold"
                style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
                Criar primeiro link
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(utm => (
              <div key={utm.id} className="bg-white rounded-xl border border-[#E7E5E0] p-4 hover:shadow-sm hover:border-[#D6D3CD] transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#EDEBE6" }}>
                    <svg className="w-4 h-4 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm text-[#0A0A0A] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>{utm.nome}</p>
                      <span className="text-[10px] text-zinc-400 shrink-0">
                        {new Date(utm.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {[utm.utmSource, utm.utmMedium, utm.utmCampaign, utm.utmContent, utm.utmTerm].filter(Boolean).map((tag, i) => (
                        <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F5F4F1", color: "#262626" }}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-2">
                      <p className="text-xs text-zinc-400 font-mono truncate flex-1">{utm.urlFinal}</p>
                      <CopyButton text={utm.urlFinal} />
                      <a href={utm.urlFinal} target="_blank" rel="noopener noreferrer"
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#0A0A0A] hover:bg-[#F5F4F1] transition-colors shrink-0"
                        title="Abrir link">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <button
                        onClick={() => { if (confirm("Excluir este link UTM?")) deleteMutation.mutate({ id: utm.id }); }}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors shrink-0"
                        title="Excluir">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <NovaUtmModal clientId={clientId} onSave={() => refetch()} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
